import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getBacktests } from '../api';
import Spinner from '../components/Spinner';
import Button from '../components/Button';

const PAGE_SIZE = 20;

function prettyTimeframe(bt) {
  const tf = bt?.fx_interval || bt?.interval || '1d';
  const map = {
    '1d': '1D', '1h': '1H', '4h': '4H', '30m': '30M', '15m': '15M', '5m': '5M',
  };
  return map[tf] || String(tf).toUpperCase();
}

function StatusPill({ status }) {
  const s = String(status || '').toLowerCase();
  const palette = {
    completed: { bg: 'rgba(34,197,94,0.15)', fg: '#22c55e' },
    running: { bg: 'rgba(124,140,255,0.15)', fg: '#7c8cff' },
    pending: { bg: 'rgba(124,140,255,0.15)', fg: '#7c8cff' },
    failed: { bg: 'rgba(239,68,68,0.15)', fg: '#ef4444' },
  };
  const c = palette[s] || { bg: 'rgba(255,255,255,0.06)', fg: 'var(--muted)' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 20,
      fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
      background: c.bg, color: c.fg,
    }}>
      {s || '—'}
    </span>
  );
}

export default function AllBacktestsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const load = useCallback((p) => {
    setLoading(true);
    setError('');
    getBacktests({ page: p, page_size: PAGE_SIZE })
      .then((res) => {
        // DRF PageNumberPagination wraps the list in { count, results }.
        const data = res.data;
        if (Array.isArray(data)) {
          setRows(data);
          setCount(data.length);
        } else {
          setRows(data?.results || []);
          setCount(data?.count || 0);
        }
      })
      .catch(() => {
        setRows([]);
        setError('Could not load your backtests.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(page); }, [load, page]);

  return (
    <div style={{ padding: '26px 26px 30px', maxWidth: 1160, margin: '0 auto' }}>
      <Helmet><title>Backtests — Algovex</title></Helmet>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12, marginBottom: 6,
      }}>
        <h1 style={{ margin: 0, fontSize: 'var(--fs-xl, 28px)' }}>Backtests</h1>
        <Button size="sm" onClick={() => navigate('/backtest')}>+ Run a backtest</Button>
      </div>
      <p style={{ color: 'var(--muted)', marginTop: 0, marginBottom: 18, fontSize: 14 }}>
        Every backtest you've run, newest first.
      </p>

      {error && (
        <div style={{ marginBottom: 14, color: 'var(--red, #ef4444)', fontSize: 13 }}>{error}</div>
      )}

      <div style={{ border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead>
              <tr style={{ color: 'var(--muted)', fontSize: 11, textAlign: 'left' }}>
                <th style={{ padding: '10px 12px' }}>Strategy</th>
                <th style={{ padding: '10px 12px' }}>Asset</th>
                <th style={{ padding: '10px 12px' }}>Timeframe</th>
                <th style={{ padding: '10px 12px' }}>Status</th>
                <th style={{ padding: '10px 12px' }}>Return</th>
                <th style={{ padding: '10px 12px' }}>Sharpe</th>
                <th style={{ padding: '10px 12px' }}>Max Drawdown</th>
                <th style={{ padding: '10px 12px' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} style={{ padding: '30px 12px', textAlign: 'center' }}>
                    <Spinner />
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: '18px 12px', color: 'var(--muted)' }}>
                    No backtests yet. Run your first one from the Backtest page.
                  </td>
                </tr>
              )}

              {!loading && rows.map((bt) => {
                const ret = bt?.return_pct == null ? null : Number(bt.return_pct);
                const dd = bt?.max_drawdown_pct == null ? null : Number(bt.max_drawdown_pct);
                const sharpe = bt?.sharpe_ratio;
                return (
                  <tr
                    key={bt.id}
                    onClick={() => navigate(`/backtests/${bt.id}`)}
                    style={{ borderTop: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}
                  >
                    <td style={{ padding: '12px', fontWeight: 600 }}>{bt?.strategy_name || 'Untitled Strategy'}</td>
                    <td style={{ padding: '12px' }}>
                      {bt?.symbol || '—'}
                      {bt?.asset_type && (
                        <span style={{ color: 'var(--muted)', fontSize: 11, marginLeft: 6, textTransform: 'uppercase' }}>
                          {bt.asset_type}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>{prettyTimeframe(bt)}</td>
                    <td style={{ padding: '12px' }}><StatusPill status={bt?.status} /></td>
                    <td style={{ padding: '12px', color: ret == null ? 'var(--muted)' : ret >= 0 ? '#22c55e' : '#ef4444' }}>
                      {ret == null ? '—' : `${ret.toFixed(2)}%`}
                    </td>
                    <td style={{ padding: '12px' }}>{sharpe == null ? '—' : Number(sharpe).toFixed(2)}</td>
                    <td style={{ padding: '12px', color: dd == null ? 'var(--muted)' : '#ef4444' }}>
                      {dd == null ? '—' : `${dd.toFixed(2)}%`}
                    </td>
                    <td style={{ padding: '12px', color: 'var(--muted)' }}>{new Date(bt.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {count > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, marginTop: 14, flexWrap: 'wrap',
        }}>
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>
            {count} backtest{count === 1 ? '' : 's'} · page {page} of {totalPages}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="sm"
              variant="secondary"
              disabled={loading || page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Prev
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={loading || page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
